import { Account } from 'src/models/account';
import { QueryResponse } from '../models/queryResponse';
import { QueryParameters } from 'src/models/queryParameters';
import { get, post, put, del } from './apiUtils';
import { mapRequestOptionsToParams } from './searchHelpers';
import { AccountDashboard } from 'src/models/accountDashboard';
import { Dashboard } from 'src/models/dashboard';
import { StrategyConversionPreview, StrategyConversionRequest } from 'src/models/strategyConversion';

export const appendDisplayCurrency = (path: string, displayCurrency?: string): string => {
    if (!displayCurrency) {
        return path;
    }

    const queryParams = new URLSearchParams({ displayCurrency });
    return `${path}?${queryParams.toString()}`;
};

export const getAccounts = async (params: QueryParameters = {}): Promise<QueryResponse<Account>> => {
    const queryParams = mapRequestOptionsToParams(params);
    return get(`/api/Accounts?${queryParams.toString()}`);
};

export const getAccount = async (id: string): Promise<Account> => {
    return get(`/api/Accounts/${id}`);
};

export const getAccountDashboard = async (id: string, displayCurrency?: string): Promise<AccountDashboard> => {
    return get(appendDisplayCurrency(`/api/Accounts/${id}/dashboard`, displayCurrency));
};

export const getDashboard = async (displayCurrency?: string): Promise<Dashboard> => {
    return get(appendDisplayCurrency('/api/dashboard', displayCurrency));
};

export const createAccount = async (accountData: Partial<Account>): Promise<Account> => {
    return post('/api/Accounts', accountData);
};

export const updateAccount = async (id: string, accountData: Partial<Account>): Promise<Account> => {
    return put(`/api/Accounts/${id}`, accountData);
};

export const deleteAccount = async (id: string): Promise<void> => {
    return del(`/api/Accounts/${id}`);
};

export const closeAccount = async (id: string): Promise<Account> => {
    return post(`/api/Accounts/${id}/close`);
};

export const syncAccount = async (accountId: string): Promise<Account> => {
    return post(`/api/accounts/${accountId}/sync`);
};

export const enableAccount = async (accountId: string): Promise<void> => {
    return post(`/api/accounts/${accountId}/enable`);
};

export const syncHoldingsFromLatestSnapshot = async (accountId: string): Promise<Account> => {
    return post(`/api/accounts/${accountId}/sync-holdings-from-latest-snapshot`);
};

export const previewStrategyConversion = async (
    accountId: string,
    request: StrategyConversionRequest
): Promise<StrategyConversionPreview> => post(`/api/accounts/${accountId}/strategy/preview`, request);

export const convertStrategy = async (accountId: string, request: StrategyConversionRequest): Promise<Account> =>
    post(`/api/accounts/${accountId}/strategy`, request);

export const undoStrategyConversion = async (accountId: string): Promise<Account> =>
    del(`/api/accounts/${accountId}/strategy/latest`);
